import { makeObservable, observable, action, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';

export interface MenuItem {
    id: number;
    menu: string;
    key: string;
    label: string;
    icon: string;
    path: string;
    type: 'route' | 'iframe' | 'external';
    config: string | null;
    prio: number;
    active: number;
    sort_order: number;
    required_permission: number | null;
    permission_name?: string;
    created: Date;
    updated: Date;
}

export interface Permission {
    id: number;
    name: string;
}

export interface MenuItemCreate {
    menu: string;
    key: string;
    label: string;
    icon?: string;
    path: string;
    type?: 'route' | 'iframe' | 'external';
    config?: string;
    prio?: number;
    active?: number;
    sort_order?: number;
    required_permission?: number | null;
}

/**
 * Store for managing menu items in the admin panel
 */
class MenuManagementStore {
    private sdk: ApplicationSdk;

    public isLoading: boolean = false;
    public menuItems: MenuItem[] = [];
    public availableMenus: string[] = [];
    public availablePermissions: Permission[] = [];
    public selectedMenu: string = 'manager';
    public selectedMenuItem: MenuItem | null = null;
    public isEditDialogVisible: boolean = false;
    public isCreateDialogVisible: boolean = false;
    public isDragMode: boolean = false;

    // Form data for creating/editing menu items
    public formData: MenuItemCreate = {
        menu: 'manager',
        key: '',
        label: '',
        icon: 'pi pi-circle',
        path: '',
        type: 'route',
        config: '',
        prio: 0,
        active: 1,
        sort_order: 0,
        required_permission: null,
    };

    constructor(sdk: ApplicationSdk) {
        this.sdk = sdk;
        makeObservable(this, {
            isLoading: observable,
            menuItems: observable,
            availableMenus: observable,
            availablePermissions: observable,
            selectedMenu: observable,
            selectedMenuItem: observable,
            isEditDialogVisible: observable,
            isCreateDialogVisible: observable,
            isDragMode: observable,
            formData: observable,
            init: action,
            loadMenuItems: action,
            loadAvailableMenus: action,
            loadAvailablePermissions: action,
            setSelectedMenu: action,
            setSelectedMenuItem: action,
            openEditDialog: action,
            openCreateDialog: action,
            closeDialogs: action,
            setFormData: action,
            createMenuItem: action,
            updateMenuItem: action,
            deleteMenuItem: action,
            reorderMenuItems: action,
            toggleDragMode: action,
            resetFormData: action,
        });
    }

    public async init(): Promise<void> {
        await this.loadAvailableMenus();
        await this.loadAvailablePermissions();
        await this.loadMenuItems();
    }

    /**
     * Load available menus
     */
    public async loadAvailableMenus(): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/available');

            runInAction(() => {
                if (response?.status === 200 && response?.json?.data) {
                    this.availableMenus = response.json.data;
                    if (
                        this.availableMenus.length > 0 &&
                        !this.availableMenus.includes(this.selectedMenu)
                    ) {
                        this.selectedMenu = this.availableMenus[0];
                    }
                }
            });
        } catch (error) {
            console.error('Error loading available menus:', error);
            this.sdk.showErrorToast('Fehler beim Laden der verfügbaren Menüs');
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Load available permissions
     */
    public async loadAvailablePermissions(): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/permissions');

            runInAction(() => {
                if (response?.status === 200 && response?.json?.data) {
                    this.availablePermissions = response.json.data;
                }
            });
        } catch (error) {
            console.error('Error loading available permissions:', error);
            this.sdk.showErrorToast('Fehler beim Laden der verfügbaren Berechtigungen');
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Load menu items for the selected menu
     */
    public async loadMenuItems(): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/item/query', {
                menu: this.selectedMenu,
                active_only: false,
            });

            runInAction(() => {
                if (response?.status === 200 && response?.json?.data) {
                    this.menuItems = response.json.data.sort(
                        (a: MenuItem, b: MenuItem) => a.sort_order - b.sort_order
                    );
                }
            });
        } catch (error) {
            console.error('Error loading menu items:', error);
            this.sdk.showErrorToast('Fehler beim Laden der Menü-Einträge');
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Set the selected menu and reload menu items
     */
    public async setSelectedMenu(menu: string): Promise<void> {
        this.selectedMenu = menu;
        await this.loadMenuItems();
    }

    /**
     * Set the selected menu item
     */
    public setSelectedMenuItem(item: MenuItem | null): void {
        this.selectedMenuItem = item;
        if (item) {
            this.formData = {
                menu: item.menu,
                key: item.key,
                label: item.label,
                icon: item.icon,
                path: item.path,
                type: item.type,
                config: item.config || '',
                prio: item.prio,
                active: item.active,
                sort_order: item.sort_order,
                required_permission: item.required_permission,
            };
        }
    }

    /**
     * Open edit dialog for a menu item
     */
    public openEditDialog(item: MenuItem): void {
        this.setSelectedMenuItem(item);
        this.isEditDialogVisible = true;
    }

    /**
     * Open create dialog for a new menu item
     */
    public openCreateDialog(): void {
        this.resetFormData();
        this.formData.menu = this.selectedMenu;
        this.isCreateDialogVisible = true;
    }

    /**
     * Close all dialogs
     */
    public closeDialogs(): void {
        this.isEditDialogVisible = false;
        this.isCreateDialogVisible = false;
        this.selectedMenuItem = null;
        this.resetFormData();
    }

    /**
     * Set form data
     */
    public setFormData(field: keyof MenuItemCreate, value: any): void {
        (this.formData as any)[field] = value;
    }

    /**
     * Reset form data to default values
     */
    public resetFormData(): void {
        this.formData = {
            menu: this.selectedMenu,
            key: '',
            label: '',
            icon: 'pi pi-circle',
            path: '',
            type: 'route',
            config: '',
            prio: 0,
            active: 1,
            sort_order: this.menuItems.length,
            required_permission: null,
        };
    }

    /**
     * Create a new menu item
     */
    public async createMenuItem(): Promise<boolean> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/item/add', this.formData);

            if (response?.status === 201) {
                this.sdk.showSuccessToast('Menü-Eintrag erfolgreich erstellt');
                this.closeDialogs();
                await this.loadMenuItems();
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Error creating menu item:', error);
            this.sdk.showErrorToast('Fehler beim Erstellen des Menü-Eintrags');
            return false;
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Update an existing menu item
     */
    public async updateMenuItem(): Promise<boolean> {
        if (!this.selectedMenuItem) return false;

        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/item/update', {
                id: this.selectedMenuItem.id,
                ...this.formData,
            });

            if (response?.status === 200) {
                this.sdk.showSuccessToast('Menü-Eintrag erfolgreich aktualisiert');
                this.closeDialogs();
                await this.loadMenuItems();
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Error updating menu item:', error);
            this.sdk.showErrorToast('Fehler beim Aktualisieren des Menü-Eintrags');
            return false;
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Delete a menu item
     */
    public async deleteMenuItem(item: MenuItem): Promise<boolean> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response = await this.sdk.fetchApi('admin', 'menu/item/delete', {
                id: item.id,
            });

            if (response?.status === 200) {
                this.sdk.showSuccessToast('Menü-Eintrag erfolgreich gelöscht');
                await this.loadMenuItems();
                return true;
            }
            return false;
        } catch (error: any) {
            console.error('Error deleting menu item:', error);
            this.sdk.showErrorToast('Fehler beim Löschen des Menü-Eintrags');
            return false;
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Reorder menu items after drag and drop
     */
    public async reorderMenuItems(reorderedItems: MenuItem[]): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            // Update sort_order for each item
            const reorderData = reorderedItems.map((item, index) => ({
                id: item.id,
                sort_order: index,
            }));

            const response = await this.sdk.fetchApi('admin', 'menu/reorder', {
                items: reorderData,
            });

            if (response?.status === 200) {
                runInAction(() => {
                    this.menuItems = reorderedItems.map((item, index) => ({
                        ...item,
                        sort_order: index,
                    }));
                });
                this.sdk.showSuccessToast('Menü-Reihenfolge erfolgreich aktualisiert');
            } else {
                await this.loadMenuItems(); // Revert on error
                this.sdk.showErrorToast('Fehler beim Sortieren der Menü-Einträge');
            }
        } catch (error) {
            console.error('Error reordering menu items:', error);
            this.sdk.showErrorToast('Fehler beim Sortieren der Menü-Einträge');
            await this.loadMenuItems(); // Reload to reset state
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Toggle drag mode
     */
    public toggleDragMode(): void {
        this.isDragMode = !this.isDragMode;
    }
}

export default MenuManagementStore;
